import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  'https://lsy9z2z7ne.execute-api.us-east-1.amazonaws.com/prod'; // Production URL as fallback

// Every request carries a deadline so a server that accepts the connection and
// never answers cannot hold a caller open forever. Callers that lock their UI
// for the duration of a request -- the Contact Support modal disables its
// backdrop, close button and Cancel while sending -- would otherwise become
// undismissable, with a page reload the only escape.
const REQUEST_TIMEOUT_MS = 30_000;

// Create a wrapper around fetchBaseQuery
const customBaseQuery: BaseQueryFn<
  FetchArgs,
  unknown,
  FetchBaseQueryError,
  object,
  FetchBaseQueryMeta
> = async (args, api, extraOptions) => {
  // Get state and token
  const state = api.getState() as AuthState;
  const token = state?.auth?.jwtToken;

  // Add access_token as query param if available
  if (token && args.url) {
    // Convert params to object if it exists, or create new object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const existingParams = (args.params as Record<string, any>) || {};
    // Only add token if not already present in params
    if (!('access_token' in existingParams)) {
      args.params = {
        ...existingParams,
        access_token: token,
      };
    }
  }

  // Get base query with our configuration
  const baseQuery = fetchBaseQuery({
    baseUrl: API_BASE_URL,
    timeout: REQUEST_TIMEOUT_MS,
    prepareHeaders: (headers) => {
      // Add Authorization header if token exists
      if (token) {
        headers.set('Authorization', token);
      }
      return headers;
    },
  });

  // Forward to the original base query
  return baseQuery(args, api, extraOptions);
};

export default customBaseQuery;
